"use client";

import { TrendingUp, Building2, Siren, Microscope, Vote, Landmark } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { useCases as data } from "@/lib/data/capabilities-narrative-engineering";

const iconMap = [Landmark, Building2, Siren, Microscope, Vote] as const;
const accents = [
  "var(--color-crimson)",
  "var(--color-amber)",
  "var(--color-emerald)",
  "var(--color-cyan)",
  "var(--color-purple)",
] as const;

export function UseCases() {
  return (
    <Section id="use-cases" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
          </div>
        </Reveal>

        <div className="mt-12 space-y-5">
          {data.cases.map((c, i) => {
            const Icon = iconMap[i % iconMap.length]!;
            const accent = accents[i % accents.length]!;
            return (
              <Reveal key={c.title} delay={i * 80}>
                <div
                  className="glass rounded-2xl p-7 flex flex-col md:flex-row gap-6"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 14%, transparent)`,
                  }}
                >
                  <div className="shrink-0">
                    <span
                      className="grid place-items-center h-14 w-14 rounded-2xl"
                      style={{
                        background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-6 w-6" style={{ color: accent }} />
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg">{c.title}</h3>
                    <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{c.body}</p>
                    <div
                      className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
                      style={{
                        background: `color-mix(in oklch, ${accent} 12%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 30%, transparent)`,
                      }}
                    >
                      <TrendingUp className="h-3.5 w-3.5" style={{ color: accent }} />
                      <span className="text-xs font-bold" style={{ color: accent }}>
                        {c.metric}
                      </span>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
